import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  #details = null;

  /** @type {MutationObserver | null} */
  #childObserver = null;

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {HTMLDetailsElement | null} */
  get details() {
    if (!this.#details || !this.contains(this.#details)) {
      this.#details = this.querySelector(':scope > details');
    }

    if (!(this.#details instanceof HTMLDetailsElement)) return null;

    return this.#details;
  }

  /** @type {HTMLElement | null} */
  get summary() {
    const details = this.details;
    if (!details) return null;

    const summary = details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) return null;

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    this.#tryInitialize();
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#childObserver?.disconnect();
    this.#childObserver = null;
    this.#isInitialized = false;
    this.#details = null;
  }

  /**
   * Initializes the accordion once its direct child <details> is available.
   */
  #tryInitialize() {
    const details = this.details;
    const summary = this.summary;

    if (!details || !summary) {
      this.#observeChildren();
      return;
    }

    if (this.#isInitialized) {
      this.#setDefaultOpenState();
      return;
    }

    this.#isInitialized = true;
    this.#childObserver?.disconnect();
    this.#childObserver = null;

    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Waits for a <details> element to be inserted during DOM morphing.
   */
  #observeChildren() {
    if (this.#childObserver) return;

    this.#childObserver = new MutationObserver(() => {
      this.#tryInitialize();
    });

    this.#childObserver.observe(this, { childList: true });
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const details = this.details;
    if (!details) return;

    const isMobile = isMobileBreakpoint();

    details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    const details = this.details;
    const summary = this.summary;

    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape && details && summary) {
      event.preventDefault();

      details.open = false;
      summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

document.addEventListener('DOMContentLoaded', function () {
  const accordions = document.querySelectorAll('.details');

  accordions.forEach((item) => {
    item.addEventListener('toggle', function () {
      if (this.open) {
        // Find the closest list/container to identify siblings
        const container = this.closest('ul, .menu-drawer__menu, .menu-drawer__inner-submenu, .drawer-localization');
        if (container) {
          const siblings = container.querySelectorAll(':scope > li > .details, :scope > li > accordion-custom > .details, :scope > .details, :scope > accordion-custom > .details');
          siblings.forEach((el) => {
            if (el !== this && el.hasAttribute('open')) {
              el.removeAttribute('open');
            }
          });
        } else {
          // Fallback to closing all others if no container found, but avoid closing ancestors/descendants
          accordions.forEach((el) => {
            if (el !== this && el.hasAttribute('open') && !el.contains(this) && !this.contains(el)) {
              el.removeAttribute('open');
            }
          });
        }
      }
    });
  });
});